import { Model } from "@ofqual/portal-core/core/types";
import { UserType } from "../redux/types";

type NullableString = string | null;
type NullableNumber = number | null;
type NullableBoolean = boolean | null;

type EventNotificationCore = {
  id: string;
  status: EventNotificationStatus;

  regulatorsAffectedOfqual: boolean;
  regulatorsAffectedCcea: boolean;
  regulatorsAffectedQw: boolean;
  regulatorsAffectedIFATE: boolean;

  orgId: NullableNumber;
  title: NullableString;
  dateOfEvent: NullableString;
  dateAware: NullableString;
  reasonForDelayInNotifying: NullableString;

  eventNotificationExamSeries: {
    id: NullableString;
    series: NullableString;
    year: NullableString;
  }[];

  affectedExamDates: {
    id: NullableString;
    examDate: NullableString;
    examTime: NullableNumber;
  }[];

  howBecameAware: NullableString;
  natureAndCause: NullableString;
  adverseEffect: NullableNumber;
  potentialOrActualAdverseEffect: NullableString;

  eventNotificationWhoIsAware: number[];
  whoIsAwareDetails: NullableString;

  dateOfNextUpdate: NullableString;
  additionalInformation: NullableString;

  informationOnQualsUnitsAffected: NullableString;

  eventNotificationCentres: {
    id: NullableString;
    centreNumberType: NullableNumber;
    centreNumber: NullableString;
    centreName: NullableString;
  }[];

  englandCentresAffected: boolean;
  englandCentresEntered: NullableNumber;
  englandCentresDirectlyAffected: NullableNumber;
  englandCentresIndirectlyAffected: NullableNumber;
  englandLearnersEntered: NullableNumber;
  englandLearnersDirectlyAffected: NullableNumber;
  englandLearnersIndirectlyAffected: NullableNumber;

  niCentresAffected: boolean;
  niCentresEntered: NullableNumber;
  niCentresDirectlyAffected: NullableNumber;
  niCentresIndirectlyAffected: NullableNumber;
  niLearnersEntered: NullableNumber;
  niLearnersDirectlyAffected: NullableNumber;
  niLearnersIndirectlyAffected: NullableNumber;

  walesCentresAffected: boolean;
  walesCentresEntered: NullableNumber;
  walesCentresDirectlyAffected: NullableNumber;
  walesCentresIndirectlyAffected: NullableNumber;
  walesLearnersEntered: NullableNumber;
  walesLearnersDirectlyAffected: NullableNumber;
  walesLearnersIndirectlyAffected: NullableNumber;

  otherCentresAffected: boolean;
  otherCentresEntered: NullableNumber;
  otherCentresDirectlyAffected: NullableNumber;
  otherCentresIndirectlyAffected: NullableNumber;
  otherLearnersEntered: NullableNumber;
  otherLearnersDirectlyAffected: NullableNumber;
  otherLearnersIndirectlyAffected: NullableNumber;

  assessmentMaterialError: boolean;
  amePaperType: NullableNumber;
  eventNotificationAMEModificationType: number[];
  eventNotificationAMEWhenIdentified: number[];
  eventNotificationAMEWhoIdentified: number[];
  eventNotificationAMEAssessmentMethod: number[];

  securityBreach: boolean;
  eventNotificationSecurityBreachMeans: number[];
  securityBreachEvidenceOfTwoPeoplePresent: NullableBoolean;
  securityBreachIncorrectPaperOutcome: NullableNumber;

  declaringNonCompliance: NullableBoolean;
  eventNotificationDeclaredNonComplianceGeneralCondition: number[];
  eventNotificationDeclaredNonComplianceGeneralSubCondition: number[];
  eventNotificationDeclaredNonComplianceQualCondition: number[];
  eventNotificationDeclaredNonComplianceSubjectCondition: number[];

  actionsTakenToIdentifyCauseAndEffect: NullableString;
  actionsTakenToMitigateAdverseEffect: NullableString;
  actionsTakenToPreventReoccurrencce: NullableString;

  amecategory: NullableNumber;

  numMarksAffected: NullableNumber;
  numMarksTotal: NullableNumber;

  createdByUpn: string;
  createdByDisplayName: string;
  createdDate: string;
  assignedToUpn: NullableString;
  assignedToDisplayName: NullableString;
  assignedToAt: NullableString;
  aoReference: NullableString;

  lastResponseDate: string;
  lastResponseByUserType?: UserType;

  numGradesDecreasedIdentified: NullableNumber;
  numGradesDecreasedAmended: NullableNumber;
  numGradesIncreasedIdentified: NullableNumber;
  numGradesIncreasedAmended: NullableNumber;

};

type EventNotificationOfqual = {
  // Ofqual fields
  inherentRiskImpact: NullableNumber;
  inherentRiskLikelihood: NullableNumber;
  residualRiskImpact: NullableNumber;
  residualRiskLikelihood: NullableNumber;

  caseSummary: NullableString;
  eventType: NullableNumber;
  eventSubType: NullableNumber;
  ofqualEventManagementActivities: NullableString;

  typeOfQualVtq: NullableBoolean;
  typeOfQualGq: NullableBoolean;

  directorateVtq: NullableBoolean;
  directorateGq: NullableBoolean;
  directorate: NullableString;

  rootCause: NullableNumber;
  rootCauseDetails: NullableString;
  eventNotificationOfqualMitigationTaken: number[];
  eventNotificationOfqualPreventReoccurence: number[];

  ofqualFollowUpDate: NullableString;
  
  riskCategory: NullableNumber;

  eventNotificationOfqualActionTaken: number[];
  ofqualActionTakenRationale: NullableString;
  adverseEffectOutcome: NullableNumber;
  agreeWithAodeclaration: NullableBoolean;
  disagreeWithAodeclarationReason: NullableString;
  finalDetermination: NullableNumber;
  aoengagement: NullableNumber;
  malpracticeAllegation: NullableNumber;

  eventNotificationIndicatedNonComplianceGeneralCondition: number[];
  eventNotificationIndicatedNonComplianceGeneralSubCondition: number[];
  eventNotificationIndicatedNonComplianceQualCondition: number[];
  eventNotificationIndicatedNonComplianceSubjectCondition: number[];

  enOfqualIndicatedNonComplianceGeneralCondition: number[];
  enOfqualIndicatedNonComplianceGeneralSubCondition: number[];
  enOfqualIndicatedNonComplianceQualCondition: number[];
  enOfqualIndicatedNonComplianceSubjectCondition: number[];
  enOfqualNonComplianceGeneralCondition: number[];
  enOfqualNonComplianceGeneralSubCondition: number[];
  enOfqualNonComplianceQualCondition: number[];
  enOfqualNonComplianceSubjectCondition: number[];

  resourceTags: number[];

  creatorEmail?: string;
};

export type EventNotification = EventNotificationCore & EventNotificationOfqual;

export const eventNotificationOfqualKeys: (keyof EventNotificationOfqual)[] = [
  "inherentRiskImpact",
  "inherentRiskLikelihood",
  "residualRiskImpact",
  "residualRiskLikelihood",
  "caseSummary",
  "eventType",
  "eventSubType",
  "ofqualEventManagementActivities",
  "typeOfQualVtq",
  "typeOfQualGq",
  "directorateVtq",
  "directorateGq",
  "directorate",
  "rootCause",
  "rootCauseDetails",
  "eventNotificationOfqualMitigationTaken",
  "eventNotificationOfqualPreventReoccurence",
  "ofqualFollowUpDate",
  "eventNotificationOfqualActionTaken",
  "ofqualActionTakenRationale",
  "adverseEffectOutcome",
  "agreeWithAodeclaration",
  "disagreeWithAodeclarationReason",
  "finalDetermination",
  "aoengagement",
  "malpracticeAllegation",
  "eventNotificationIndicatedNonComplianceGeneralCondition",
  "eventNotificationIndicatedNonComplianceGeneralSubCondition",
  "eventNotificationIndicatedNonComplianceQualCondition",
  "eventNotificationIndicatedNonComplianceSubjectCondition",
  "enOfqualIndicatedNonComplianceGeneralCondition",
  "enOfqualIndicatedNonComplianceGeneralSubCondition",
  "enOfqualIndicatedNonComplianceQualCondition",
  "enOfqualIndicatedNonComplianceSubjectCondition",
  "enOfqualNonComplianceGeneralCondition",
  "enOfqualNonComplianceGeneralSubCondition",
  "enOfqualNonComplianceQualCondition",
  "enOfqualNonComplianceSubjectCondition" 
];

export enum EventNotificationStatus {
  New = 0,
  Active = 1,
  Closed = 2,
}

export const fromApiModel = (model: Model): EventNotification => {
  return {
    ...model,
  } as EventNotification;
};

export const toApiModel = (item: EventNotification): EventNotification => {
  // if no values are entered for the oneToMany relationship we need to filter it out of the array
  item.eventNotificationCentres = item.eventNotificationCentres?.filter(
    (centre) =>
      centre.centreNumberType !== null
  );

  // the database doesn't like nulls
  if (item.eventNotificationCentres) {
      item.eventNotificationCentres = item.eventNotificationCentres.map(
        (centre) => ({
          ...centre,
          centreName:  centre.centreName !== null ? centre.centreName : "",
          centreNumber: centre.centreNumber !== null ? centre.centreNumber : "",
        })
      );
    }

  item.eventNotificationExamSeries = item.eventNotificationExamSeries?.filter(
    (examSeries) => examSeries.series !== null && examSeries.year !== null
  );



  return {
    ...item,
  } as EventNotification;
};
